import React, { useEffect, useRef, useState } from 'react';
import { FormData, Program } from '../types';
import './FormModal.css';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: Program;
  onSubmit: (formData: FormData) => void;
  formSubmitted: boolean;
}

const FormModal: React.FC<FormModalProps> = ({ 
  isOpen, 
  onClose, 
  program, 
  onSubmit,
  formSubmitted 
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    status: 'Student',
    collegeName: '',
    yearOfPassout: '',
    companyName: '',
    yearsOfExperience: '',
    programId: program.id,
    programTitle: program.title
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isStudent, setIsStudent] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New state for loading indicator
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Add animation class
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 500);
      return () => clearTimeout(timer);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    // Reset form when program changes or form is reopened
    if (isOpen && !formSubmitted) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        status: 'Student',
        collegeName: '',
        yearOfPassout: '',
        companyName: '',
        yearsOfExperience: '',
        programId: program.id,
        programTitle: program.title
      });
      setErrors({});
      setIsStudent(true);
      setIsLoading(false); // Reset loading state when reopening modal
    }
  }, [isOpen, program, formSubmitted]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone should be 10 digits';
    }
    
    if (isStudent) {
      if (!formData.collegeName.trim()) {
        newErrors.collegeName = 'College name is required';
      }
      if (!formData.yearOfPassout.trim()) {
        newErrors.yearOfPassout = 'Year of passout is required';
      }
    } else {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      if (!formData.yearsOfExperience.trim()) {
        newErrors.yearsOfExperience = 'Years of experience is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === 'status') {
      setIsStudent(value === 'Student');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setIsLoading(true); // Set loading state to true when form is submitted
        // Log the form data before submission for debugging
        console.log('Submitting form data:', formData);
        await onSubmit(formData); // Wait for submission to complete
        // The parent component will set formSubmitted to true if successful
      } catch (error) {
        console.error('Form submission error:', error);
        setIsLoading(false); // Reset loading state if there's an error
        alert('There was an error submitting your form. Please try again.');
      }
    }
  };

  const handleCloseModal = () => {
    // Don't allow closing when loading
    if (isLoading) return;
    
    // Add closing animation
    if (modalRef.current) {
      modalRef.current.style.animation = 'scaleOut 0.3s forwards';
      setTimeout(onClose, 250);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={isLoading ? undefined : handleCloseModal}>
      <div 
        className={`modal-content ${animating ? 'animating' : ''}`} 
        onClick={e => e.stopPropagation()}
        ref={modalRef}
      >
        <button 
          className="modal-close" 
          onClick={handleCloseModal}
          disabled={isLoading} // Disable close button when loading
        >×</button>
        
        {formSubmitted ? (
          <div className="form-success">
            <div className="icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your application for <strong>{program.title}</strong> has been submitted.</p>
            <p>Please check your email for further instructions.</p>
            <button className="btn btn-primary" onClick={handleCloseModal}>Close</button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>{program.title}</h2>
              <p>Fill out the form below to apply for this program</p>
            </div>
            
            <form onSubmit={handleSubmit} className="program-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    disabled={isLoading}
                  />
                  {errors.name && <div className="error-message">{errors.name}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@email.com"
                    disabled={isLoading}
                  />
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    disabled={isLoading}
                  />
                  {errors.phone && <div className="error-message">{errors.phone}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="status">Current Status</label>
                  <select
                    id="status"
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={isLoading}
                  >
                    <option value="Student">Student</option>
                    <option value="Experienced">Experienced</option>
                  </select>
                </div>
              </div>
              
              {/* Conditional Fields with Animation */}
              <div className={`conditional-field ${isStudent ? 'active' : ''}`}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="collegeName">College/University Name</label>
                    <input
                      type="text"
                      id="collegeName"
                      name="collegeName"
                      className="form-control"
                      value={formData.collegeName}
                      onChange={handleChange}
                      placeholder="Enter your college name"
                      disabled={isLoading}
                    />
                    {errors.collegeName && <div className="error-message">{errors.collegeName}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="yearOfPassout">Year of Passing Out</label>
                    <input
                      type="text"
                      id="yearOfPassout"
                      name="yearOfPassout"
                      className="form-control"
                      value={formData.yearOfPassout}
                      onChange={handleChange}
                      placeholder="e.g., 2026"
                      disabled={isLoading}
                    />
                    {errors.yearOfPassout && <div className="error-message">{errors.yearOfPassout}</div>}
                  </div>
                </div>
              </div>
              
              <div className={`conditional-field ${!isStudent ? 'active' : ''}`}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyName">Current/Previous Company</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      className="form-control"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Enter company name"
                      disabled={isLoading}
                    />
                    {errors.companyName && <div className="error-message">{errors.companyName}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="yearsOfExperience">Years of Experience</label>
                    <input
                      type="text"
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      className="form-control"
                      value={formData.yearsOfExperience}
                      onChange={handleChange}
                      placeholder="e.g., 2.5"
                      disabled={isLoading}
                    />
                    {errors.yearsOfExperience && <div className="error-message">{errors.yearsOfExperience}</div>}
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <><span className="spinner"></span> Submitting...</>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default FormModal;